import { useApiClient } from "@/hooks/useApiClient";
import useSWR from "swr";
import useSWRMutation, { type SWRMutationResponse } from "swr/mutation";
import type {
  UserConfigAPIResponse,
  UserConfigUpdateAPIResponse,
  UserConfigUpdateRequest,
} from "@/features/accountSettings/types/schemas";

const url = "/user/config";

/**
 * ユーザー設定の取得API
 * @returns 成功ならUserConfigAPIResponse、失敗ならpostを行って新規作成し、作成した結果が返る
 */
export const UserConfigGetAPI = (): UserConfigAPIResponse | undefined => {
  const { apiClient } = useApiClient();

  const fetcher = async (url: string): Promise<UserConfigAPIResponse> => {
    const res = await apiClient
      .get<UserConfigAPIResponse>(url)
      .catch(async (reason) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (reason.response.status === 404) {
          return await apiClient.post<UserConfigAPIResponse>(url);
        }
        throw reason;
      });
    return res.data;
  };
  const { data } = useSWR<UserConfigAPIResponse, number | undefined>(
    url,
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );
  return data;
};

/**
 * ユーザー設定の更新API
 * @returns trigger()が返ってくるのでtrigger(data: UserConfigUpdateRequest)で設定を送信することができます
 */
export const UserConfigUpdateAPI = (): SWRMutationResponse<
  UserConfigUpdateAPIResponse,
  never,
  "/user/config",
  UserConfigUpdateRequest
> => {
  const { apiClient } = useApiClient();

  const fetcher = async (
    url: string,
    { arg }: { arg: UserConfigUpdateRequest }
  ): Promise<UserConfigUpdateAPIResponse> => {
    const response = await apiClient.put<UserConfigUpdateAPIResponse>(url, arg);
    return response.data;
  };

  return useSWRMutation(url, fetcher, { revalidate: false });
};
